import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import './styles/cookie-consent.css';
import cookieConsent from './lib/cookie-consent';
import { GTMService } from './lib/gtm';

// Function to check analytics consent
function hasAnalyticsConsent(): boolean {
  try {
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('cc_cookie='));
    
    if (consentCookie) {
      const consentValue = consentCookie.split('=')[1];
      const parsed = JSON.parse(decodeURIComponent(consentValue));
      return parsed.categories?.includes('analytics') || false;
    }
    return false;
  } catch {
    return false;
  }
}

// Get Sentry configuration with consent-aware settings
function getSentryConfig() {
  const hasConsent = hasAnalyticsConsent();
  const isDev = import.meta.env.DEV;
  
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || (isDev ? 'development' : 'production'),
    
    // Enable automatic IP address collection only with consent
    sendDefaultPii: hasConsent || isDev,
    
    // Set release (use git commit SHA in production)
    release: `rulevia@${import.meta.env.VITE_APP_VERSION || 'unknown'}`,
    
    // Performance monitoring - respect consent in production
    tracesSampleRate: isDev ? 1.0 : (hasConsent ? 0.1 : 0.0),
    
    // Session replay - requires analytics consent
    replaysSessionSampleRate: isDev ? 1.0 : (hasConsent ? 0.1 : 0.0),
    replaysOnErrorSampleRate: isDev ? 1.0 : (hasConsent ? 1.0 : 0.0),
    
    // Browser profiling - respect consent
    profilesSampleRate: isDev ? 1.0 : (hasConsent ? 0.1 : 0.0),
    
    // Integrations - conditionally include based on consent
    integrations: [
      Sentry.browserTracingIntegration({
        // Set up automatic route change tracking for React Router
        enableInp: hasConsent || isDev,
        enableLongTask: hasConsent || isDev,
      }),
      ...(hasConsent || isDev ? [
        Sentry.replayIntegration({
          // Capture sessions only with consent
          maskAllText: false,
          blockAllMedia: false,
        })
      ] : []),
    ],
    
    // Filter out known errors/noisy errors
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Chrome extensions
      'chrome-extension',
      'moz-extension',
      // Network errors that are not actionable
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'ResizeObserver loop limit exceeded',
    ],
    
    // Don't capture errors from certain URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
    
    // Set context for all events
    initialScope: {
      tags: {
        component: 'frontend',
        platform: 'web',
      },
    },
    
    // Only enable Sentry if DSN is provided
    enabled: !!import.meta.env.VITE_SENTRY_DSN,
  };
}

// Initialize Sentry with consent-aware configuration
Sentry.init(getSentryConfig());

// Listen for consent changes and update Sentry settings dynamically
if (typeof window !== 'undefined') {
  window.addEventListener('cookieConsentUpdate', (event: any) => {
    const { consent } = event.detail;

    // Update Sentry configuration dynamically instead of reinitializing
    const hasConsent = consent.analytics || false;
    const isDev = import.meta.env.DEV;

    // Update global scope with new consent status
    Sentry.setTag('analytics_consent', hasConsent);
    Sentry.setTag('consent_updated', new Date().toISOString());

    // Note: Sentry integrations (replay, tracing) cannot be dynamically changed
    // They are set during initial Sentry.init() call and remain for the session
  });
}

// Initialize Cookie Consent
async function initializeCookieConsent() {
  try {
    // Initialize GTM service
    GTMService.initialize();

    // Register consent service with GTM for consent checking
    GTMService.registerConsentService(cookieConsent);

    // Initialize cookie consent banner
    await cookieConsent.init();

    // Enable debugging in development
    if (import.meta.env.DEV) {
      cookieConsent.debug();
    }
  } catch (error) {
    console.error('❌ Failed to initialize cookie consent:', error);
    console.error('Full error:', error);
  }
}

// Initialize cookie consent when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCookieConsent);
} else {
  initializeCookieConsent();
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
